/**
 * Manual Finance payment confirmation: payment truth vs customer receipt email.
 * Automated provider/webhook paths are unchanged and do not use these modes.
 */

export const FINANCE_PAYMENT_RECEIPT_MODES = ["record_only", "record_and_send"] as const;
export type FinancePaymentReceiptMode = (typeof FINANCE_PAYMENT_RECEIPT_MODES)[number];

export const DEFAULT_FINANCE_PAYMENT_RECEIPT_MODE: FinancePaymentReceiptMode = "record_and_send";

export function isFinancePaymentReceiptMode(value: unknown): value is FinancePaymentReceiptMode {
  return value === "record_only" || value === "record_and_send";
}

/** Mask customer email for confirmation UI (never show full address in staff copy). */
export function maskCustomerEmailForReceiptConfirm(email: string | null | undefined): string {
  const raw = String(email ?? "").trim();
  if (!raw || !raw.includes("@")) return "(no email on file)";
  const [local, domain] = raw.split("@");
  if (!local || !domain) return "(no email on file)";
  const visible = local.length <= 2 ? `${local[0] ?? "*"}*` : `${local.slice(0, 2)}***`;
  return `${visible}@${domain}`;
}

export type ManualMarkPaidReceiptOutcome = {
  mode: FinancePaymentReceiptMode;
  customerNotificationRequested: boolean;
  sent: boolean;
  warning?: string | null;
  skippedReason?: string | null;
  invoiceNumber?: string | null;
  recipientMasked?: string | null;
};

export function buildManualMarkPaidSuccessMessage(outcome: ManualMarkPaidReceiptOutcome): string {
  if (outcome.mode === "record_only") {
    return "Payment recorded. No customer receipt was sent.";
  }
  if (outcome.sent) {
    return "Payment recorded and customer receipt sent.";
  }
  if (outcome.skippedReason === "already_acknowledged") {
    return "Payment recorded. Customer receipt was already sent earlier.";
  }
  return "Payment recorded, but the customer receipt needs retry.";
}
